import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export interface Report {
  id: string;
  reporterId: string;
  targetType: 'POST' | 'COMMENT' | 'USER' | 'MESSAGE';
  targetId: string;
  reason: string;
  description?: string;
  status: 'PENDING' | 'REVIEWING' | 'RESOLVED' | 'DISMISSED';
  createdAt: string;
  updatedAt: string;
  reporter?: {
    id: string;
    username: string;
    email: string;
    avatar?: string;
  };
  post?: any;
  comment?: any;
  user?: any;
}

export interface ModeratorAction {
  id: string;
  moderatorId: string;
  targetUserId?: string;
  targetPostId?: string;
  targetCommentId?: string;
  reportId?: string;
  actionType: 'WARN_USER' | 'SUSPEND_USER' | 'REMOVE_CONTENT' | 'DISMISS' | 'ESCALATE' | 'RESTORE_CONTENT';
  reason?: string;
  notes?: string;
  createdAt: string;
  moderator?: {
    id: string;
    username: string;
    email: string;
  };
  targetUser?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface UserWarning {
  id: string;
  userId: string;
  moderatorId: string;
  reason: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  user?: {
    id: string;
    username: string;
    email: string;
  };
  moderator?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface ModeratorStats {
  openReports: number;
  resolvedToday: number;
  totalActions: number;
  activeWarnings: number;
}

export const moderatorService = {
  async getReports(params: { status?: string; targetType?: string; limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/moderator/reports`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { reports: Report[]; pagination: any };
  },

  async reviewReport(reportId: string, data: {
    action: 'DISMISS' | 'REMOVE_CONTENT' | 'WARN_USER' | 'SUSPEND_USER' | 'ESCALATE';
    reason?: string;
    notes?: string;
    warningSeverity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    suspendUntil?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/moderator/reports/${reportId}/review`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getActions(params: { limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/moderator/actions`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { actions: ModeratorAction[]; pagination: any };
  },

  async issueWarning(data: {
    userId: string;
    reason: string;
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    expiresAt?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/moderator/warnings`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { warning: UserWarning };
  },

  async getUserWarnings(userId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/moderator/warnings/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { warnings: UserWarning[] };
  },

  async getStats() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/moderator/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as ModeratorStats;
  },
};
